import { DataType, UserPermission, SelectOption } from '@globaleris/ui.library'
import { Grid, makeStyles, useTheme } from '@material-ui/core'
import moment from 'moment'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EnumCommodity } from '../../api'
import { NotificationMessageIcons } from '../../components'
import Page from '../../components/page/page'
import PageHeader from '../../components/page/pageHeader'
import CommodityFilter from '../../components/shared/commodityFilter/commodityFilter'
import CustomFilter from '../../components/shared/customFilter/customFilter'
import DateFilter from '../../components/shared/dateFilter/dateFilter'
import TabsWithPermissions, { Tab } from '../../components/tabsWithPermissions/tabsWithPermissions'
import { MapToArray } from '../../helpers'
import { enumToMapValues, enumValueToKey } from '../../helpers/enumHelper'
import { hasPermission } from '../../helpers/permissionsHelper'
import { usePageState } from '../../hooks/usePageState'
import useRetries from '../../hooks/useRetries'
import { DateFilters, DatePeriod, GlobalNotification } from '../../model'
import { Resources } from '../../model/RBAC/constants'
import { getActiveCommodities } from '../../redux'
import {
  storeActiveTabForCurrentPage,
  storeActiveTabPowerForCurrentPage,
  storeCustomFilterForCurrentPage,
  storeDateFilterForCurrentPage
} from '../../redux/helpers'
import {
  RemitLngActivity,
  RemitLngPlanned,
  RemitStorage,
  RemitTable1,
  RemitTable2
} from './components'
import { CoresReports } from './components/coresReports/coresReports'
import {
  RegulatoryReports,
  RegulatoryReportsPower,
  remitReportOperationModel
} from './regulatoryReports.Model'
import {
  fetchRegulatoryReportsRemitLngListItems,
  fetchRegulatoryReportsRemitStorageListItems,
  fetchRegulatoryReportsRemitTableListItems,
  fetchLogisticElements
} from './regulatoryReports.service'
import { styles } from './regulatoryReports.styles'
import { RegulatoryReportsGasCommodity } from './regulatoryReportsGasCommodity'
import { RegulatoryReportsPowerCommodity } from './regulatoryReportsPowerCommodity'
import { RegulatoryReportsPowerCommodityCnmc } from './regulatoryReportsPowerCommodityCnmc'

export interface RegulatoryReportsPageProps {}

export const RegulatoryReportsPage: React.FC<RegulatoryReportsPageProps> = props => {
  const [t] = useTranslation(['regulatoryReports'])

  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()

  const commodities = getActiveCommodities()

  const initialCommodityFilter: EnumCommodity =
    commodities !== undefined && commodities?.length > 0
      ? enumValueToKey(EnumCommodity, commodities[0])
      : EnumCommodity.GasNatural

  const [commodity, setCommodity] = useState<EnumCommodity>(initialCommodityFilter)

  const { dateFilter, customFilter, activeTab, activeTabPower } = usePageState()

  const [logisticElements, setLogisticElements] = useState<SelectOption[]>([])

  // const [currentTab, setCurrentTab] = useState<number>(activeTab ?? 0)

  // const [currentTabPower, setCurrentTabPower] = useState<number>(activeTabPower ?? 0)

  const [isFetching, setIsFetching] = useState<boolean>(true)

  const [dateRange, setDateRange] = React.useState<DatePeriod>({
    start: dateFilter?.initDate || moment(new Date()).subtract(7, 'days').toDate(),
    end: dateFilter?.endDate || moment().toDate()
  })

  const getInitialFormDependencies = async () => {
    setLogisticElements(await fetchLogisticElements())
  }

  React.useEffect(() => {
    getInitialFormDependencies()
  }, [])

  const regulatoryReportsTabsArray = MapToArray(enumToMapValues(RegulatoryReports))
  const tabsDefinition: Map<number, Tab> = new Map(
    regulatoryReportsTabsArray.map((reportType, tabIndex) => [
      tabIndex,
      {
        id: reportType.value,
        label: t(`regulatoryReports:tabs.${reportType.name.toLowerCase()}`),
        hasPermission: hasPermission(Resources.InformesRegulatorios, [
          UserPermission.EDIT,
          UserPermission.READ_ONLY
        ])
      }
    ])
  )

  const tabsDefinitionPowerTabsArray = MapToArray(enumToMapValues(RegulatoryReportsPower))
  const tabsDefinitionPower: Map<number, Tab> = new Map(
    tabsDefinitionPowerTabsArray.map((reportType, tabIndex) => [
      tabIndex,
      {
        id: reportType.value,
        label: t(`regulatoryReports:commodityPower.tabs.${reportType.name.toLowerCase()}`),
        hasPermission: hasPermission(Resources.InformesRegulatorios, [
          UserPermission.EDIT,
          UserPermission.READ_ONLY
        ])
      }
    ])
  )

  const [globalNotification, setGlobalNotification] = useState<GlobalNotification | undefined>(
    undefined
  )

  //#region Hooks

  // React.useEffect(() => {
  //   if (activeTab) {
  //     setactiveTab(activeTab);
  //   }

  //   if(activeTabPower) {
  //     setactiveTabPower(activeTabPower);
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  //#endregion

  //#region Handlers

  const onSelectDateHandler = (
    startDate: Date,
    endDate: Date,
    activeDateFilter?: DateFilters
  ): void => {
    setDateRange({ start: startDate, end: endDate })
    storeDateFilterForCurrentPage({
      activeDateFilter: activeDateFilter,
      initDate: startDate,
      endDate: endDate
    })
  }

  const onRemitTableChildFetching = (childIsFetching: boolean) => {
    setIsFetching(childIsFetching)
  }

  const onGlobalNotificationChangeHandler = (value: GlobalNotification | undefined) => {
    setGlobalNotification(value)
  }

  const [startRetriesTimer, stopRetriesTimer, isRetriesTimerActive] = useRetries(
    async () => {
      // setTimeout(async () => {
      //   // setRefreshChild(prevState => !prevState)
      // }, 7000)
    },
    5000,
    {
      autoStart: false,
      immediate: true,
      onRetry: () => {
        onGlobalNotificationChangeHandler({
          icon: NotificationMessageIcons.LOADING,
          message: t('regulatoryReports:globalNotifications.retries.messages.onRetry'),
          actions: [
            {
              label: t('regulatoryReports:globalNotifications.retries.actions.cancel'),
              onClick: () => {
                stopRetriesTimer()
                onGlobalNotificationChangeHandler({
                  message: t('regulatoryReports:globalNotifications.retries.messages.onStop'),
                  actions: [
                    {
                      label: t('regulatoryReports:globalNotifications.retries.actions.refresh'),
                      onClick: async () => {
                        startRetriesTimer()
                      }
                    },
                    {
                      label: t('regulatoryReports:globalNotifications.retries.actions.close'),
                      onClick: () => {
                        onGlobalNotificationChangeHandler(undefined)
                      }
                    }
                  ]
                })
              }
            }
          ]
        })
      },
      onFinishRetries: () => {
        onGlobalNotificationChangeHandler({
          icon: NotificationMessageIcons.INFO,
          message: t('regulatoryReports:globalNotifications.retries.messages.onFinish'),
          actions: [
            {
              label: t('regulatoryReports:globalNotifications.retries.actions.refresh'),
              onClick: async () => {
                startRetriesTimer()
              }
            },
            {
              label: t('regulatoryReports:globalNotifications.retries.actions.close'),
              onClick: () => {
                onGlobalNotificationChangeHandler(undefined)
              }
            }
          ]
        })
      },
      onError: () => {
        onGlobalNotificationChangeHandler({
          icon: NotificationMessageIcons.ERROR,
          message: t('regulatoryReports:globalNotifications.retries.messages.onError'),
          actions: [
            {
              label: t('regulatoryReports:globalNotifications.retries.actions.refresh'),
              onClick: async () => {
                startRetriesTimer()
              }
            }
          ]
        })
      }
    }
  )

  //#endregion

  //#region Functions

  const renderTabContent = (tabIndex: number) => {
    switch (tabIndex) {
      case RegulatoryReports.REMITTABLE1:
        return (
          <RemitTable1
            startDate={dateRange.start}
            endDate={dateRange.end}
            onFetching={onRemitTableChildFetching}
            onGlobalNotificationChange={onGlobalNotificationChangeHandler}
          ></RemitTable1>
        )
      case RegulatoryReports.REMITTABLE2:
        return (
          <RemitTable2
            startDate={dateRange.start}
            endDate={dateRange.end}
            onFetching={onRemitTableChildFetching}
            onGlobalNotificationChange={onGlobalNotificationChangeHandler}
          ></RemitTable2>
        )
      case RegulatoryReports.REMITSTORAGE:
        return (
          <RemitStorage
            startDate={dateRange.start}
            endDate={dateRange.end}
            onFetching={onRemitTableChildFetching}
            onGlobalNotificationChange={onGlobalNotificationChangeHandler}
            idElemLog={customFilter?.idElemLog}
          ></RemitStorage>
        )
      case RegulatoryReports.REMITLNGACTIVITY:
        return (
          <RemitLngActivity
            startDate={dateRange.start}
            endDate={dateRange.end}
            onFetching={onRemitTableChildFetching}
            onGlobalNotificationChange={onGlobalNotificationChangeHandler}
          ></RemitLngActivity>
        )
      case RegulatoryReports.REMITLNGPLANNED:
        return (
          <RemitLngPlanned
            startDate={dateRange.start}
            endDate={dateRange.end}
            onFetching={onRemitTableChildFetching}
            onGlobalNotificationChange={onGlobalNotificationChangeHandler}
          ></RemitLngPlanned>
        )
      case RegulatoryReports.CNMC:
        return (
          <RegulatoryReportsGasCommodity
            commodity={commodity}
            onGlobalNotificationChange={onGlobalNotificationChangeHandler}
          />
        )
      case RegulatoryReports.CORES:
        return (
          <CoresReports
            commodity={commodity}
            onGlobalNotificationChangeHandler={onGlobalNotificationChangeHandler}
            onRemitTableChildFetching={onRemitTableChildFetching}
          />
        )
      default:
        return null
    }
  }

  const renderTabContentPower = (tabIndex: number) => {
    switch (tabIndex) {
      case RegulatoryReportsPower.MINCOTUR:
        return (
          <RegulatoryReportsPowerCommodity
            commodity={commodity}
            onGlobalNotificationChange={onGlobalNotificationChangeHandler}
          />
        )
      case RegulatoryReportsPower.CNMC_POWER:
        return (
          <RegulatoryReportsPowerCommodityCnmc
            commodity={commodity}
            onGlobalNotificationChange={onGlobalNotificationChangeHandler}
          />
        )
      default:
        return null
    }
  }

  const getReports = async (
    startDate: Date,
    endDate: Date,
    idElemLog: number | undefined,
    tabIndex: number
  ): Promise<remitReportOperationModel[] | null> => {
    switch (tabIndex) {
      case RegulatoryReports.REMITTABLE1:
      case RegulatoryReports.REMITTABLE2:
        return (await fetchRegulatoryReportsRemitTableListItems(startDate, endDate, false)).data
      case RegulatoryReports.REMITSTORAGE:
        return (
          await fetchRegulatoryReportsRemitStorageListItems(
            startDate,
            endDate,
            undefined,
            idElemLog
          )
        ).data
      case RegulatoryReports.REMITLNGACTIVITY:
      case RegulatoryReports.REMITLNGPLANNED:
        return (await fetchRegulatoryReportsRemitLngListItems(startDate, endDate)).data
      default:
        return []
    }
  }

  const countReports = async (
    startDate: Date,
    endDate: Date,
    idElemLog: number | undefined
  ): Promise<number | undefined> => {
    const reports = await getReports(startDate, endDate, idElemLog, activeTab ?? 0)
    return reports ? reports.length : undefined
  }

  const onApplyCustomFilter = async (filterToApply: any) => {
    setDateRange({ start: filterToApply.startDate, end: filterToApply.endDate })
    storeCustomFilterForCurrentPage(filterToApply)
  }

  const onSelectCommodityHandler = (commodity: EnumCommodity): void => {
    setCommodity(commodity)
    // storeCommodityFilterForCurrentPage(commodity);
  }

  const renderCommodities = () => {
    if (commodities !== undefined && commodities.length > 1) {
      return (
        <CommodityFilter
          commodities={[
            { commodity: enumValueToKey(EnumCommodity, commodities[0]) },
            { commodity: enumValueToKey(EnumCommodity, commodities[1]) }
          ]}
          commodity={commodity}
          enabled={!isFetching}
          inactive={false}
          onSelectCommodity={onSelectCommodityHandler}
        />
      )
    }
    return
  }

  const onPrevalidateCustomFilter = async (filterToApply: any): Promise<boolean> => {
    const start = new Date(filterToApply.startDate)
    const end = new Date(filterToApply.endDate)

    const totalCount = await countReports(start, end, filterToApply.idElemLog)
    if (totalCount !== undefined && totalCount < 1000) {
      return true
    }
    return false
  }

  //#endregion

  const searchFilterInputs = [
    {
      name: 'idElemLog',
      label: t('regulatoryReports:searchFilter.infraestructura'),
      type: DataType.SELECT,
      selectOptions: logisticElements
    }
  ]

  return (
    <Page notification={globalNotification}>
      {commodities && (
        <>
          <PageHeader title={t('regulatoryReports:title')}>
            <Grid container justify="flex-end">
              {renderCommodities()}
            </Grid>
          </PageHeader>
          {activeTab !== RegulatoryReports.CNMC && commodity !== EnumCommodity.Electricidad && (
            <Grid container justify="flex-end">
              <div className={classes.dateFilterContainer}>
                <DateFilter
                  filters={[
                    { filter: DateFilters.ACTIVES }
/*                    { filter: DateFilters.TODAY },
                    { filter: DateFilters.YESTERDAY },
                    { filter: DateFilters.LASTWEEK },
                    { filter: DateFilters.MONTH },
                    { filter: DateFilters.LASTMONTH }
*/                  ]}
                  initialFilter={
                    dateFilter?.activeDateFilter === null ||
                    dateFilter?.activeDateFilter === undefined
                      ? DateFilters.ACTIVES
                      : dateFilter.activeDateFilter
                  }
                  enabled={!isFetching}
                  inactive={customFilter}
                  onSelectDate={onSelectDateHandler}
                ></DateFilter>
              </div>
              <CustomFilter
                onApplyFilter={onApplyCustomFilter}
                onPrevalidateFilter={onPrevalidateCustomFilter}
                appliedFilter={customFilter}
                enabled={!isFetching}
                additionalFilters={
                  activeTab === RegulatoryReports.REMITSTORAGE ? searchFilterInputs : undefined
                }
              ></CustomFilter>
            </Grid>
          )}
          <TabsWithPermissions
            tabs={commodity !== EnumCommodity.Electricidad ? tabsDefinition : tabsDefinitionPower}
            tabDefault={
              commodity !== EnumCommodity.Electricidad ? (activeTab ?? 0) : (activeTabPower ?? 0)
            }
            onTabChange={index => {
              if (commodity === EnumCommodity.Electricidad && activeTabPower !== index) {
                // setCurrentTabPower(index);
                storeActiveTabPowerForCurrentPage(index)
                setGlobalNotification(undefined)
              } else if (commodity !== EnumCommodity.Electricidad && activeTab !== index) {
                // setCurrentTabPower(index);
                storeActiveTabForCurrentPage(index)
                setGlobalNotification(undefined)
              }
            }}
          />

          {commodity !== EnumCommodity.Electricidad
            ? renderTabContent(activeTab ?? 0)
            : renderTabContentPower(activeTabPower ?? 0)}
        </>
      )}
    </Page>
  )
}
